/**
 * How many BottomSheets are currently mounted, and therefore whether the app
 * shell is allowed to resize underneath one.
 *
 * Aug 20 2026. Index.tsx releases --vv-height on the RAW Median close signal,
 * un-debounced, so chat dismiss feels immediate (:799-823). iOS raw-closes and
 * reopens the keyboard between EVERY field, so with a sheet open that release
 * fires on every field hop and the shell cycles 577 -> 878 -> 577.
 *
 * The sheet itself no longer consumes the var and does not move — measured at
 * zero DOM mutations across a blip. What moves is the page BEHIND it: TasksTab
 * is `h-full overflow-y-auto` inside that shell, so growing the shell by 301px
 * shrinks its max scroll by 301px and the browser CLAMPS scrollTop. Shrinking
 * back does not restore it. The background therefore ratchets a little further
 * on every field hop, visible through the sheet's `bg-black/50` backdrop.
 *
 * This registry lets Index.tsx suppress ONLY the un-debounced raw release, and
 * only while a sheet is open. Two things are deliberately preserved:
 *
 *   - Chat is untouched. It never has a BottomSheet open, so the count is 0 and
 *     the raw release fires exactly as before. Its dismiss contract is intact.
 *   - A genuine keyboard close still releases the var, via the DEBOUNCED writer
 *     (Index.tsx:759-779) once useStableKeyboardOffset confirms the close after
 *     CLOSE_CONFIRM_MS. The gate suppresses the twitchy path, not the real one.
 */
let openSheetCount = 0;

/**
 * Call on sheet mount; the returned function releases on unmount. Safe to use
 * directly as a useEffect cleanup, and idempotent so React's development
 * double-invoke cannot drive the count negative.
 */
export function registerOpenSheet(): () => void {
  openSheetCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    openSheetCount = Math.max(0, openSheetCount - 1);
  };
}

export function isAnySheetOpen(): boolean {
  return openSheetCount > 0;
}

/**
 * Index.tsx's raw-close release of --vv-height, gated on no sheet being open.
 * Returns true if the property was actually released.
 *
 * The DOM write lives here rather than inline in Index.tsx so the rule can be
 * asserted without mounting Index — a mutation that drops the gate at the call
 * site is otherwise invisible to the suite, which is exactly what happened the
 * first time this was written.
 */
export function releaseVvHeightOnRawClose(): boolean {
  if (isAnySheetOpen()) return false;
  document.documentElement.style.removeProperty("--vv-height");
  return true;
}

/** Test-only reset. Never called from app code. */
export function __resetOpenSheetsForTests(): void {
  openSheetCount = 0;
}
